/**
 * Session 30.37 — FastInput: a drop-in for `Input` that never drops
 * keystrokes inside very large components.
 *
 * Problem: the Staff / Owner dashboard is one ~8k-line component. A
 * classic controlled <Input value={state}> re-renders that whole tree on
 * EVERY keystroke. On tablet WebViews that takes 100-300 ms per key, so
 * typed characters get lost, the soft keyboard appears to "stop
 * working", and under memory pressure the WebView can crash.
 *
 * Fix: the DOM input owns the text while the user types (uncontrolled),
 * and the parent state is updated through `startTransition`, which React
 * treats as interruptible / low priority — typing stays instant and the
 * heavy re-render happens in the gaps. On blur we commit synchronously so
 * any button pressed right after typing always sees the final value.
 * When the parent resets the value while the field is NOT focused (e.g.
 * dialog reopen, "clear" buttons) we mirror that into the DOM.
 *
 * Same props/signature as `Input` (`value`, `onChange(e)`), so swapping
 * is a one-word change.
 */
import * as React from "react";
import { Input } from "@/components/ui/input";

export const FastInput = React.forwardRef(function FastInput({ value, onChange, onBlur, ...props }, ref) {
  const innerRef = React.useRef(null);
  const setRefs = React.useCallback((el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  // Mirror external resets into the DOM when the user isn't typing.
  React.useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const v = value == null ? "" : String(value);
    if (typeof document !== "undefined" && document.activeElement === el) return;
    if (el.value !== v) el.value = v;
  }, [value]);

  const timerRef = React.useRef(null);
  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const emit = (v, sync) => {
    if (typeof onChange !== "function") return;
    const evt = { target: { value: v }, currentTarget: { value: v } };
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (sync) { onChange(evt); return; }
    // Fast path: low-priority (interruptible) update so typing never stalls…
    React.startTransition(() => onChange(evt));
    // …and a guaranteed normal-priority commit shortly after the last
    // keystroke, so buttons/derived values are final by the time the user
    // can reach them even if the transition kept getting interrupted.
    timerRef.current = setTimeout(() => { timerRef.current = null; onChange(evt); }, 120);
  };

  return (
    <Input
      ref={setRefs}
      defaultValue={value == null ? "" : String(value)}
      onChange={(e) => emit(e.target.value, false)}
      onBlur={(e) => { emit(e.target.value, true); if (typeof onBlur === "function") onBlur(e); }}
      {...props}
    />
  );
});
